import {
    Component, OnInit, OnDestroy, Input, HostListener, ElementRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService, AppNotification } from '../../services/notification.service';
import { DomToast } from '../../../core/utils/dom-toast.util';
import { getNotificationDedupKey } from '../../../core/utils/notification-dedup';

@Component({
    selector: 'app-notification-bell',
    templateUrl: './notification-bell.component.html',
    styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {

    @Input() userType: 'dairy' | 'member' = 'dairy';

    unreadCount   = 0;
    notifications: AppNotification[] = [];
    panelOpen     = false;
    enabled       = false;

    private destroy$    = new Subject<void>();
    private seenIds     = new Set<number>();
    private initialized = false;
    private pollCount   = 0;   // counts actual HTTP poll responses received

    constructor(
        private notifService: NotificationService,
        private elRef: ElementRef
    ) {}

    ngOnInit(): void {
        this.notifService.unreadCount$.pipe(takeUntil(this.destroy$))
            .subscribe(c => {
                if (c > this.unreadCount) {
                    this.playChime();
                }
                this.unreadCount = c;
            });

        this.notifService.notifications$.pipe(takeUntil(this.destroy$))
            .subscribe(n => {
                // BehaviorSubject emits [] immediately — that's pollCount 1 (after increment).
                // Skip that empty emit, seed seenIds on the first real HTTP response (pollCount 2),
                // then show toasts only for genuinely new IDs on every subsequent response.
                this.pollCount++;

                if (this.pollCount === 1) {
                    // BehaviorSubject empty emit — ignore, not a real HTTP response yet
                } else if (!this.initialized) {
                    // First real HTTP response: seed all existing IDs silently, no toasts
                    n.forEach(notif => this.seenIds.add(notif.id));
                    this.initialized = true;
                } else {
                    const pending = n.filter(notif => !this.seenIds.has(notif.id) && !notif.is_read);
                    const unique = pending.filter((notif, idx, arr) => arr.findIndex(item => getNotificationDedupKey(item) === getNotificationDedupKey(notif)) === idx);
                    unique.forEach(notif => {
                        this.seenIds.add(notif.id);
                        this.showToast(notif);
                    });
                }
                this.notifications = n;
            });

        this.notifService.enabled$.pipe(takeUntil(this.destroy$))
            .subscribe(e => this.enabled = e);
    }

    private showToast(notif: AppNotification): void {
        DomToast.notify(notif.title, notif.body);
    }

    private playChime(): void {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            // Short two-tone chime: high note then slightly lower
            const times = [{ freq: 880, start: 0, dur: 0.12 }, { freq: 660, start: 0.13, dur: 0.18 }];
            times.forEach(({ freq, start, dur }) => {
                const osc   = ctx.createOscillator();
                const gain  = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type      = 'sine';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0, ctx.currentTime + start);
                gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + start + 0.01);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
                osc.start(ctx.currentTime + start);
                osc.stop(ctx.currentTime + start + dur);
            });
        } catch { /* AudioContext not available — silent fail */ }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    togglePanel(): void {
        this.panelOpen = !this.panelOpen;
        if (this.panelOpen) {
            if (this.userType === 'member') {
                this.notifService.loadMemberNotifications();
            } else {
                this.notifService.loadNotifications();
            }
        }
    }

    markAllRead(): void {
        this.notifService.markAllRead(this.userType);
    }

    markOneRead(notif: AppNotification): void {
        if (!notif.is_read) {
            this.notifService.markOneRead(notif.id, this.userType);
        }
    }

    trackById(_: number, n: AppNotification): number { return n.id; }

    // Close panel on outside click
    @HostListener('document:click', ['$event'])
    onDocClick(event: MouseEvent): void {
        if (!this.elRef.nativeElement.contains(event.target)) {
            this.panelOpen = false;
        }
    }
}
